import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Box, Card, Chip, Collapse, Typography, useColorScheme } from "@mui/material";
import {
    AccessTimeRounded,
    ChildCare,
    Face2Outlined,
    Face3Outlined,
    Face6Outlined,
    SentimentSatisfiedAltOutlined,
    SupervisedUserCircleOutlined
} from "@mui/icons-material";
import { fixTextToSlug, replacePersianNumbers, toPriceWithComma } from "../../../helper/utils";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import GympinPinIcon from "../../../components/GympinPinIcon";
import { useIncredibleCountdown } from "../../../helper/useCountdownTimer";

// کامپوننت مجزا و بهینه‌سازی‌شده برای تایمر معکوس جهت جلوگیری از رندر مجدد کل کارت
const IncredibleCountdownBadge = React.memo(({ incredibleDate, intervalHours }) => {
    const timerParts = useIncredibleCountdown(incredibleDate, intervalHours);
    if (!timerParts) return null;

    return (
        <Box
            sx={{
                position: "absolute",
                bottom: 10,
                insetInlineStart: 10,
                display: "flex",
                alignItems: "center",
                gap: 0.8,
                zIndex: 2
            }}
        >
            {/* ثانیه */}
            <Box
                sx={{
                    minWidth: 26,
                    height: 26,
                    borderRadius: "6px",
                    bgcolor: "rgba(58, 58, 58, 0.48)",
                    backdropFilter: "blur(6px)",
                    border: "1px solid rgba(161, 17, 17, 0.94)",
                    color: "#ffffff",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: "0.78rem",
                    fontWeight: 900,
                    fontFamily: "monospace",
                    boxShadow: "0 2px 8px rgba(0, 0, 0, 0.35)",
                    px: 1.2,
                    py: 2
                }}
            >
                {timerParts.seconds}
            </Box>
            {/* دقیقه */}
            <Box
                sx={{
                    minWidth: 26,
                    height: 26,
                    borderRadius: "6px",
                    bgcolor: "rgba(58, 58, 58, 0.48)",
                    backdropFilter: "blur(6px)",
                    border: "1px solid rgba(161, 17, 17, 0.94)",
                    color: "#ffffff",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: "0.78rem",
                    fontWeight: 900,
                    fontFamily: "monospace",
                    boxShadow: "0 2px 8px rgba(0, 0, 0, 0.35)",
                    px: 1.2,
                    py: 2
                }}
            >
                {timerParts.minutes}
            </Box>

            {/* ساعت */}
            <Box
                sx={{
                    minWidth: 26,
                    height: 26,
                    borderRadius: "6px",
                    bgcolor: "rgba(58, 58, 58, 0.48)",
                    backdropFilter: "blur(6px)",
                    border: "1px solid rgba(161, 17, 17, 0.94)",
                    color: "#ffffff",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: "0.78rem",
                    fontWeight: 900,
                    fontFamily: "monospace",
                    boxShadow: "0 2px 8px rgba(0, 0, 0, 0.35)",
                    px: 1.2,
                    py: 2
                }}
            >
                {timerParts.hours}
            </Box>
        </Box>
    );
});

const _ep_CAROUSEL_SIMPLE_LIST_ITEM_SIMPLE_SUBSCRIBE = ({ item, isDark: isDarkProp }) => {
    const navigate = useNavigate();
    const { mode, systemMode } = useColorScheme();
    const isDark = isDarkProp !== undefined ? isDarkProp : (mode === "system" ? systemMode : mode) === "dark";
    const [showActiveTime, setShowActiveTime] = useState(false);

    const activeTimes = item?.ActiveTimes || item?.Place?.ActiveTimes;
    const sportsList = item?.Sports || item?.Place?.Sports || [];

    const incredibleInterval = useSelector(state => {
        const list = state.settings?.server?.Settings;
        const conf = list?.find(s => s.Key === "TICKET_INCREDIBLES_INTERVAL");
        return conf?.Value ? Number(conf.Value) : 50;
    });

    const discountAmount = useMemo(() => {
        if (item?.ValuePrice && item?.Price && item.ValuePrice > item.Price) {
            return item.ValuePrice - item.Price;
        }
        return null;
    }, [item?.ValuePrice, item?.Price]);

    const placeImage = useMemo(() => {
        const defaultMedia = item?.Place?.Multimedias?.find(m => m.default) || item?.Place?.Multimedias?.[0];
        if (defaultMedia?.Url) {
            const separator = defaultMedia.Url.includes('?') ? '&' : '?';
            return `${defaultMedia.Url}${separator}width=350`;
        }
        return "https://api.gympin.ir/resource/image?Id=11&width=350";
    }, [item?.Place?.Multimedias]);

    const placeUrl = "/place/" + item?.Place?.Id + "-" + fixTextToSlug(item?.Place?.Name);

    const renderGenderBadge = (gender) => {
        switch (gender) {
            case "MALE":
                return (
                    <Chip
                        size="small"
                        icon={<SentimentSatisfiedAltOutlined sx={{ fontSize: "0.85rem !important", color: "#0284c7 !important" }} />}
                        label="آقایان"
                        sx={{
                            bgcolor: "rgba(2, 132, 199, 0.08)",
                            color: "#0369a1",
                            fontWeight: 600,
                            fontSize: "0.68rem",
                            px: 1,
                            height: 22,
                            borderRadius: "6px",
                            border: "1px solid rgba(2, 132, 199, 0.15)"
                        }}
                    />
                );
            case "FEMALE":
                return (
                    <Chip
                        size="small"
                        icon={<Face3Outlined sx={{ fontSize: "0.85rem !important", color: "#e11d48 !important" }} />}
                        label="خانم‌ها"
                        sx={{
                            bgcolor: "rgba(225, 29, 72, 0.08)",
                            color: "#be123c",
                            fontWeight: 600,
                            fontSize: "0.68rem",
                            px: 1,
                            height: 22,
                            borderRadius: "6px",
                            border: "1px solid rgba(225, 29, 72, 0.15)"
                        }}
                    />
                );
            case "BOYS":
                return (
                    <Chip
                        size="small"
                        icon={<Face6Outlined sx={{ fontSize: "0.85rem !important", color: "#0284c7 !important" }} />}
                        label="پسرها"
                        sx={{
                            bgcolor: "rgba(2, 132, 199, 0.08)",
                            color: "#0369a1",
                            fontWeight: 600,
                            fontSize: "0.68rem",
                            px: 1,
                            height: 22,
                            borderRadius: "6px"
                        }}
                    />
                );
            case "GIRLS":
                return (
                    <Chip
                        size="small"
                        icon={<Face2Outlined sx={{ fontSize: "0.85rem !important", color: "#e11d48 !important" }} />}
                        label="دخترها"
                        sx={{
                            bgcolor: "rgba(225, 29, 72, 0.08)",
                            color: "#be123c",
                            fontWeight: 600,
                            fontSize: "0.68rem",
                            px: 1,
                            height: 22,
                            borderRadius: "6px"
                        }}
                    />
                );
            case "KIDS":
                return (
                    <Chip
                        size="small"
                        icon={<ChildCare sx={{ fontSize: "0.85rem !important", color: "#ca8a04 !important" }} />}
                        label="کودکان"
                        sx={{
                            bgcolor: "rgba(202, 138, 4, 0.08)",
                            color: "#a16207",
                            fontWeight: 600,
                            fontSize: "0.68rem",
                            px: 1,
                            height: 22,
                            borderRadius: "6px"
                        }}
                    />
                );
            case "NONE":
                return (
                    <Chip
                        size="small"
                        icon={<SupervisedUserCircleOutlined sx={{ fontSize: "0.85rem !important", color: isDark ? "#a3a3a3 !important" : "#525252 !important" }} />}
                        label="همه"
                        sx={{
                            bgcolor: isDark ? "rgba(255, 255, 255, 0.06)" : "rgba(0, 0, 0, 0.05)",
                            color: isDark ? "#d4d4d4" : "#525252",
                            fontWeight: 600,
                            fontSize: "0.68rem",
                            px: 1,
                            height: 22,
                            borderRadius: "6px"
                        }}
                    />
                );
            default:
                return null;
        }
    };

    return (
        <Card
            onClick={() => navigate(placeUrl)}
            dir="rtl"
            sx={{
                mx: 1,
                my: 1.5,
                borderRadius: "20px",
                overflow: "hidden",
                isolation: "isolate",
                transform: "translateZ(0)",
                WebkitMaskImage: "-webkit-radial-gradient(white, black)",
                bgcolor: "background.paper",
                border: "1px solid",
                borderColor: "divider",
                boxShadow: "0 8px 24px -4px rgba(0, 0, 0, 0.08), 0 2px 6px -1px rgba(0, 0, 0, 0.04)",
                transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                cursor: "pointer",
                display: "flex",
                flexDirection: "column",
                outline: "none",
                "&:hover": {
                    transform: "translateY(-5px)",
                    boxShadow: "0 16px 36px -6px rgba(0, 0, 0, 0.12)",
                    borderColor: "rgba(231, 51, 62, 0.25)"
                },
                "&:hover .subscribe-card-img": {
                    transform: "scale(1.05)"
                },
                "&:active": {
                    transform: "scale(0.985)"
                }
            }}
        >
            {/* تصویر کارت با تایمر و بج تخفیف شناور */}
            <Box
                sx={{
                    position: "relative",
                    width: "100%",
                    height: 165,
                    borderTopLeftRadius: "19px",
                    borderTopRightRadius: "19px",
                    overflow: "hidden",
                    isolation: "isolate",
                    transform: "translateZ(0)",
                    WebkitMaskImage: "-webkit-radial-gradient(white, black)",
                    bgcolor: "action.hover"
                }}
            >
                <Box
                    component="img"
                    className="subscribe-card-img"
                    src={placeImage}
                    alt={item?.Place?.Name || "اشتراک"}
                    loading="lazy"
                    decoding="async"
                    sx={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                        display: "block",
                        borderTopLeftRadius: "19px",
                        borderTopRightRadius: "19px",
                        transition: "transform 0.5s cubic-bezier(0.4, 0, 0.2, 1)"
                    }}
                />

                {/* تایمر معکوس شگفت‌انگیز به صورت کامپوننت مجزا جهت جلوگیری از رندر مجدد کل کارت */}
                {item?.Incredible && (
                    <IncredibleCountdownBadge
                        incredibleDate={item.Incredible}
                        intervalHours={incredibleInterval}
                    />
                )}

                {/* تگ شناور منطقه در گوشه چپ پایین */}
                {item?.Place?.Location?.Name && (
                    <Box
                        sx={{
                            position: "absolute",
                            bottom: 10,
                            insetInlineEnd: 10,
                            display: "flex",
                            alignItems: "center",
                            gap: 0.4,
                            bgcolor: "rgba(58, 58, 58, 0.48)",
                            backdropFilter: "blur(8px)",
                            px: 1,
                            py: 0.3,
                            borderRadius: "16px",
                            border: "1px solid rgba(255, 255, 255, 0.18)",
                            boxShadow: "0 4px 12px rgba(0, 0, 0, 0.25)",
                            zIndex: 1
                        }}
                    >
                        <GympinPinIcon size={14} primaryColor="#E3343F" secondaryColor="#ffffff" />
                        <Typography
                            variant="caption"
                            sx={{
                                fontSize: "0.7rem",
                                fontWeight: 700,
                                color: "#ffffff",
                                lineHeight: 1.2,
                                maxWidth: 90,
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap"
                            }}
                        >
                            {item.Place.Location.Name}
                        </Typography>
                    </Box>
                )}
            </Box>

            {/* بدنه محتوای کارت */}
            <Box sx={{ p: 2, pb: 1.5, flex: 1, display: "flex", flexDirection: "column" }}>
                {/* نام مجموعه ورزشی در سمت راست */}
                <Typography
                    variant="caption"
                    sx={{
                        color: "text.secondary",
                        fontWeight: 700,
                        fontSize: "0.78rem",
                        mb: 0.3,
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis"
                    }}
                >
                    {item?.Place?.Name}
                </Typography>

                {/* عنوان پلن اشتراک در سمت راست */}
                <Typography
                    variant="h6"
                    component="h3"
                    sx={{
                        fontWeight: 800,
                        fontSize: "0.98rem",
                        color: "text.primary",
                        mb: 1.2,
                        lineHeight: 1.4,
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis"
                    }}
                >
                    {item?.Name}
                </Typography>

                {/* خط اول: ساعت کاری در راست و جنسیت در چپ */}
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        minHeight: 28,
                        mb: 0.8,
                        width: "100%"
                    }}
                >
                    {/* دکمه ساعات کاری در سمت راست */}
                    {activeTimes ? (
                        <Box
                            onClick={(e) => {
                                e.stopPropagation();
                                setShowActiveTime(!showActiveTime);
                            }}
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 0.4,
                                bgcolor: showActiveTime ? "rgba(231, 51, 62, 0.12)" : "action.hover",
                                color: showActiveTime ? "primary.main" : "text.secondary",
                                px: 1,
                                py: 0.35,
                                borderRadius: "8px",
                                cursor: "pointer",
                                transition: "all 0.2s ease",
                                flexShrink: 0,
                                "&:hover": {
                                    bgcolor: "rgba(231, 51, 62, 0.12)",
                                    color: "primary.main"
                                }
                            }}
                        >
                            <AccessTimeRounded sx={{ fontSize: "0.85rem" }} />
                            <Typography sx={{ fontSize: "0.68rem", fontWeight: 700 }}>
                                ساعت کار
                            </Typography>
                        </Box>
                    ) : <Box />}

                    {/* بج جنسیت در سمت چپ */}
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, flexShrink: 0 }}>
                        {item?.Gender != null ? renderGenderBadge(item.Gender) : renderGenderBadge("NONE")}
                    </Box>
                </Box>

                {/* خط دوم: ورزش‌ها راست‌چین و تعداد مابقی بلافاصله در انتهای متن */}
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "flex-start",
                        gap: 0.6,
                        minHeight: 22,
                        mb: 1.2,
                        width: "100%",
                        overflow: "hidden",
                        flexWrap: "nowrap"
                    }}
                >
                    {sportsList && sportsList.length > 0 ? (
                        <>
                            {sportsList.slice(0, 3).map((sport, idx) => (
                                <React.Fragment key={`sport-${idx}`}>
                                    {idx > 0 && (
                                        <Box
                                            component="span"
                                            sx={{
                                                width: 3.5,
                                                height: 3.5,
                                                borderRadius: "50%",
                                                bgcolor: "#e7333e",
                                                flexShrink: 0
                                            }}
                                        />
                                    )}
                                    <Typography
                                        component="span"
                                        sx={{
                                            fontSize: "0.72rem",
                                            fontWeight: 500,
                                            color: "text.secondary",
                                            whiteSpace: "nowrap",
                                            textOverflow: "ellipsis",
                                            overflow: "hidden",
                                            maxWidth: 95,
                                            lineHeight: 1.2
                                        }}
                                    >
                                        {sport.Name}
                                    </Typography>
                                </React.Fragment>
                            ))}
                            {sportsList.length > 3 && (
                                <Box
                                    component="span"
                                    dir="rtl"
                                    sx={{
                                        display: "inline-flex",
                                        alignItems: "center",
                                        flexDirection: "row",
                                        color: "text.disabled",
                                        fontSize: "0.68rem",
                                        fontWeight: 600,
                                        whiteSpace: "nowrap",
                                        flexShrink: 0
                                    }}
                                >
                                    <Box component="span">+</Box>
                                    <Box component="span">{replacePersianNumbers(String(sportsList.length - 3))}</Box>
                                </Box>
                            )}
                        </>
                    ) : null}
                </Box>

                {/* باکس تاشوی ساعات کاری */}
                {activeTimes && (
                    <Collapse in={showActiveTime} timeout="auto" unmountOnExit>
                        <Alert
                            icon={<AccessTimeRounded fontSize="small" />}
                            severity="info"
                            sx={{
                                mb: 1.5,
                                py: 0.5,
                                px: 1.25,
                                fontSize: "0.72rem",
                                borderRadius: "10px",
                                bgcolor: "rgba(2, 132, 199, 0.06)",
                                color: "#0369a1",
                                border: "1px solid rgba(2, 132, 199, 0.15)",
                                "& .MuiAlert-icon": { color: "#0284c7", p: 0 }
                            }}
                        >
                            {activeTimes}
                        </Alert>
                    </Collapse>
                )}
            </Box>

            {/* فوتر: بخش قیمت و تخفیف چسبیده به پایین و لبه‌های کارت بدون حاشیه سفید */}
            {discountAmount != null && discountAmount > 0 ? (
                <Box
                    sx={{
                        width: "100%",
                        borderTop: "1px solid #e2e2e2ff",
                        py: 0.9,
                        px: 1.75,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-evenly",
                        mt: "auto"
                    }}
                >
                    {/* ستون قیمت در سمت راست: شروع از در راست، و قیمت تخفیف‌نخورده دقیقاً بالای قیمت تخفیف‌خورده */}
                    <Box sx={{ display: "flex", alignItems: "flex-end", gap: 0.6 }}>
                        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-start", gap: 0.15 }}>
                            {item?.ValuePrice && item.ValuePrice > item.Price && (
                                <Typography
                                    sx={{
                                        color: "#811623ff",
                                        textDecoration: "line-through",
                                        fontSize: "0.68rem",
                                        fontWeight: 600,
                                        lineHeight: 1
                                    }}
                                >
                                    {toPriceWithComma(item.ValuePrice)}
                                </Typography>
                            )}
                            <Box sx={{ display: "flex", alignItems: "center", gap: 0.35 }}>
                                <Typography
                                    sx={{
                                        color: "#811623ff",
                                        fontSize: "1.02rem",
                                        fontWeight: 900,
                                        lineHeight: 1
                                    }}
                                >
                                    {toPriceWithComma(item?.Price)}
                                </Typography>
                                <Box
                                    component="img"
                                    src="/assets/images/svg/toman.svg"
                                    alt="تومان"
                                    sx={{
                                        width: 13,
                                        height: 13,
                                        display: "block",
                                        filter: "brightness(0) invert(1)"
                                    }}
                                />
                            </Box>
                        </Box>
                    </Box>

                    {/* خط عمودی جداکننده تخفیف از قیمت */}
                    <Box
                        sx={{
                            width: "1px",
                            height: 32,
                            borderLeft: "1px dashed #811623ff",
                            my: "auto",
                            mx: 1
                        }}
                    />

                    {/* بخش تخفیف با آیکون در سمت چپ */}
                    <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", flexShrink: 0 }}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.3 }}>
                            <Typography sx={{ color: "#811623ff", fontSize: "0.85rem", fontWeight: 900, lineHeight: 1.1 }}>
                                {toPriceWithComma(discountAmount)}
                            </Typography>
                            <Box
                                component="img"
                                src="/assets/images/svg/toman.svg"
                                alt="تومان"
                                sx={{
                                    width: 12,
                                    height: 12,
                                    display: "block",
                                    filter: "brightness(0) invert(1)"
                                }}
                            />
                        </Box>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.35, mt: 0.2 }}>
                            <Box
                                component="img"
                                src="/assets/images/discountIcon.svg"
                                alt="تخفیف"
                                sx={{
                                    width: 13,
                                    height: 13,
                                    display: "block"
                                }}
                            />
                            <Typography sx={{ color: "#811623ff", fontSize: "0.68rem", fontWeight: 900, lineHeight: 1 }}>
                                تخفیف
                            </Typography>
                        </Box>
                    </Box>
                </Box>
            ) : (
                <Box
                    sx={{
                        width: "100%",
                        borderTop: "1px solid",
                        borderColor: "divider",
                        py: 1,
                        px: 2,
                        mt: "auto",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: 0.6,
                        minHeight: 46
                    }}
                >
                    {item?.Price ? (
                        <>
                            <Typography
                                sx={{
                                    color: "text.primary",
                                    fontSize: "1.08rem",
                                    fontWeight: 900,
                                    lineHeight: 1
                                }}
                            >
                                {toPriceWithComma(item?.Price)}
                            </Typography>
                            <Box
                                component="img"
                                src="/assets/images/svg/toman.svg"
                                alt="تومان"
                                sx={{
                                    width: 14,
                                    height: 14,
                                    display: "block",
                                    filter: isDark ? "brightness(1.25)" : "none"
                                }}
                            />
                        </>
                    ) : (
                        <Typography sx={{ color: "text.disabled", fontSize: "0.82rem", fontWeight: 600 }}>
                            بدون پلن فعال
                        </Typography>
                    )}
                </Box>
            )}
        </Card>
    );
};

export default React.memo(_ep_CAROUSEL_SIMPLE_LIST_ITEM_SIMPLE_SUBSCRIBE);

